import { useMutation, useQuery } from '@apollo/client/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  GQL_REGENERATE_WEBHOOK_SECRET,
  GQL_SAVE_WEBHOOK_ENDPOINT,
  GQL_SEND_WEBHOOK_TEST,
  GQL_WEBHOOK_DELIVERIES,
  GQL_WEBHOOK_ENDPOINT,
} from '~/gql/webhooks/webhooks'
import { useSyncOnChange } from '~/hooks/use-sync-on-change'

/** L'erreur que renvoie l'API (URL refusée, destination inactive…), ou un message générique. */
const messageOf = (error: unknown, fallback: string) => (error instanceof Error && error.message ? error.message : fallback)

/**
 * L'état et les actions de l'écran des webhooks (#5). La destination n'a pas
 * d'identifiant que le cache saurait rapprocher : chaque écriture relit donc la
 * destination et le journal, sinon l'écran restait figé sur « aucune destination ».
 */
export function useWebhookSettings() {
  const { t } = useTranslation()
  const { data, loading } = useQuery(GQL_WEBHOOK_ENDPOINT, { fetchPolicy: 'network-only' })
  const { data: logData, refetch: refetchLog } = useQuery(GQL_WEBHOOK_DELIVERIES, { fetchPolicy: 'network-only' })
  const refetchQueries = [GQL_WEBHOOK_ENDPOINT, GQL_WEBHOOK_DELIVERIES]
  const [save, { loading: saving }] = useMutation(GQL_SAVE_WEBHOOK_ENDPOINT, { refetchQueries, awaitRefetchQueries: true })
  const [regenerate] = useMutation(GQL_REGENERATE_WEBHOOK_SECRET)
  const [queueTest, { loading: testing }] = useMutation(GQL_SEND_WEBHOOK_TEST, { refetchQueries: [GQL_WEBHOOK_DELIVERIES] })

  const endpoint = data?.webhookEndpoint ?? null
  const [form, setForm] = useState({ url: '', enabled: false })
  const [revealed, setRevealed] = useState<string | null>(null)
  useSyncOnChange(endpoint, stored => setForm({ url: stored.url, enabled: stored.enabled }))

  const run = async (action: () => Promise<void>) => {
    try {
      await action()
    } catch (error) {
      toast.error(messageOf(error, t('webhooks.saveError')))
    }
  }

  return {
    loading,
    endpoint,
    deliveries: logData?.webhookDeliveries ?? [],
    form,
    setForm,
    revealed,
    saving,
    testing,
    refreshLog: () => void refetchLog(),
    save: () => run(async () => {
      const result = await save({ variables: { input: form } })
      if (result.data?.saveWebhookEndpoint.secret) setRevealed(result.data.saveWebhookEndpoint.secret)
      toast.success(t('webhooks.saved'))
    }),
    regenerate: () => run(async () => {
      const result = await regenerate()
      setRevealed(result.data?.regenerateWebhookSecret ?? null)
    }),
    sendTest: () => run(async () => {
      await queueTest()
      toast.success(t('webhooks.testQueued'))
    }),
  }
}
